import { Suspense, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import type { Session } from '../core/auth/AuthService';
import { AppsGrid } from './AppsGrid';
import { GlobalSidebar } from './GlobalSidebar';
import { HOME, isActivePath } from './services';
import { SPACES, ShellContext, type ShellUser, type ShellValue, type Space } from './ShellContext';
import { TabBar } from './TabBar';
import { useIsDesktop } from './useMediaQuery';
import { useSidebarState } from './useSidebarState';
import './shell.css';

const fallback = <div className="spin" role="status" aria-label="Chargement" style={{ margin: '40vh auto' }} />;

/**
 * Coque de navigation : sidebar globale sur bureau, barre d'onglets sur mobile. Elle enveloppe l'écran courant
 * (`<Outlet/>`) sans rien savoir du contenu des modules.
 */
export function AppShell({ session, user, onLogout }: { session: Session | null; user: ShellUser; onLogout?: () => void }) {
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  const { mode, toggle } = useSidebarState(pathname);
  const [space, setSpace] = useState<Space>(SPACES[0]!);
  const [apps, setApps] = useState<{ pathname: string } | null>(null);

  // La grille Apps appartient à l'écran où on l'a ouverte : changer d'adresse la referme.
  const appsOpen = !desktop && apps?.pathname === pathname;
  const toggleApps = (): void => setApps(appsOpen ? null : { pathname });

  const value = useMemo<ShellValue>(() => ({ user, session, onLogout, space, setSpace }), [user, session, onLogout, space]);
  // L'Accueil défile dans la coque ; un service gère lui-même sa mise en page et sa hauteur.
  const fill = !isActivePath(pathname, HOME.path);

  return (
    <ShellContext.Provider value={value}>
      <div className="shell" data-layout={desktop ? 'desktop' : 'mobile'}>
        {desktop ? <GlobalSidebar mode={mode} onToggle={toggle} pathname={pathname} /> : null}
        <main className="shell__main" data-fill={fill && !appsOpen} id="contenu">
          {appsOpen ? (
            <AppsGrid onNavigate={() => setApps(null)} />
          ) : (
            <Suspense fallback={fallback}>
              <Outlet />
            </Suspense>
          )}
        </main>
        {desktop ? null : <TabBar pathname={pathname} appsOpen={appsOpen} onToggleApps={toggleApps} onHome={() => setApps(null)} />}
      </div>
    </ShellContext.Provider>
  );
}
